// Page changes in Material 3's transition patterns (Dominik 21.10.2026: "nach Material 3 alles gescheit"): between
// the tabs "fade through", into a page and back out of it "shared axis X" (styles/components.css, page changes). The
// new page comes in, the old one goes at once: M3 lets it fade within its first 75 ms anyway. The browser's View
// Transitions were tried and dropped: while one runs, every tap goes to the document, so a quick second tap (another
// tab, a switch on the new page) was lost for 450 ms. The page stays usable while it moves.
import { parentOf, type SubPage } from '../core/index.js';
import type { TabId } from './tabs.js';

export type NavMotion = 'tab' | 'forward' | 'back';

/** --dur-nav and a frame: until then a page that appears takes part in the change */
const NAV_MS = 480;

/** 0 for a tab's main page, 1 for its pages, 2 for a page under a page (a bean under "Bohnen") */
export function pageDepth(p: SubPage): number {
  let depth = 0;
  for (let q = p; q.page !== 'main' && depth < 4; q = parentOf(q)) depth++;
  return depth;
}

/** The pattern for going from one place to another: deeper is forward, shallower back, sideways a tab change. */
export function navMotionOf(
  from: { tab: TabId; page: SubPage },
  to: { tab: TabId; page: SubPage },
): NavMotion {
  const a = pageDepth(from.page);
  const b = pageDepth(to.page);
  if (b > a) return 'forward';
  if (b < a) return 'back';
  return from.tab === to.tab ? 'forward' : 'tab';
}

let timer = 0;

/**
 * Marks the page change (data-nav on the root: the CSS animates the page that appears) and applies `update`. No mark
 * without a kind (the same place again, the browser's own back swipe) or while a sheet is open over the page.
 */
export function navigate(kind: NavMotion | null, update: () => void): void {
  const root = document.documentElement;
  if (kind && document.querySelector('dialog[open]') === null) {
    root.dataset.nav = kind;
    window.clearTimeout(timer);
    timer = window.setTimeout(() => delete root.dataset.nav, NAV_MS);
  }
  update();
}
